import { Col, Popover, Row, Typography } from "antd";
import dayjs from "config/dayjs";
import { analytics } from "config/firebase/firebase";
import { logEvent } from "firebase/analytics";
import { MenuSlug, MenuTypes } from "menus/interface";
import { useAppActionRole } from "providers/action-role";
import { useActivitySidebar } from "providers/active-sidebar";
import { useNotification } from "providers/app-notification";
import { TPropertyNotification } from "providers/app-notification/interface";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getMenuClick,
  getProperty,
  removeMenuClick,
  setMenuClick,
  setMenuCode,
  setMenuSlug,
  usePostMenuClick,
} from "service/auth";
import styled from "styled-components";
import tw from "twin.macro";

export interface SiderMenuItemProps {
  key: MenuTypes;
  title: MenuTypes;
  icon?: React.ComponentType;
  to: string;
  children?: Omit<SiderMenuItemProps, "icon" | "children">[];
  code: string;
  slug?: string;
}

const SiderMenuItemWrapper = styled.div<{ $active: boolean }>`
  ${tw`cursor-pointer transition-all pl-6`};
  svg * {
    ${({ $active }) => ($active ? tw`fill-primary` : "")};
  }
  .ant-typography {
    color: ${({ $active }) => ($active ? "black" : "#5b5b5b")};
    font-weight: ${({ $active }) => ($active ? "500" : "")};
  }
  background-color: ${({ $active }) => ($active ? "#fff0f0" : "")};
  &:hover {
    .ant-typography {
      color: black;
      font-weight: 500;
    }
  }
`;

const NotificationItem = styled.div`
  width: 35px;
  border-radius: 20px;
  color: #fff;
  background-color: #f52a53;
`;

const SiderMenuItem: React.FC<{ item: SiderMenuItemProps }> = ({ item }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setMenu } = useAppActionRole();
  const { propertyId } = useParams<{ propertyId?: string }>();
  const { to, title, children, icon, slug } = item;
  const property = getProperty();
  const storedData = getMenuClick();
  const dateNow = dayjs().startOf("day").toISOString();
  const postMenuClickData = usePostMenuClick();

  const Icon = icon as React.ComponentType<React.SVGProps<SVGSVGElement>>;

  const { notification, resetNotificationBySlug } = useNotification();

  const { setActiveKey } = useActivitySidebar();

  const onNavigate = () => {
    if (property?.uuid) {
      logEvent(analytics, "menu_backoffice_property", {
        [`${property?.uuid}`]: item.code,
      });
    } else {
      logEvent(analytics, "menu_backoffice_central", { central: item.code });
    }

    setMenu(item);
    setMenuCode(item.code);
    setActiveKey?.(undefined);
    setMenuSlug(item.slug || "");

    onSetMenuClick();

    const resetProps = { slug: slug as MenuSlug, propertyUuid: property?.uuid };
    resetNotificationBySlug(resetProps);

    if (children) {
      return;
    }

    if (propertyId) {
      return navigate(to.replace(":propertyId", propertyId), { replace: true });
    }

    return navigate(to, { replace: true });
  };

  const onSetMenuClick = () => {
    const uuidProperty = property?.uuid || "";
    const isPropertyPage =
      pathname.includes("/property/") && to.includes("/property/:propertyId/");
    const propertyRecords = storedData?.propertyRecord || [];
    const findProperty = propertyRecords.find(
      (e) => e.propertyUuid === uuidProperty
    );

    if (storedData) {
      if (storedData.recordDate !== dateNow) {
        postMenuClickData.mutate(storedData, {
          onSuccess: () => {
            removeMenuClick();
          },
        });
      }
    }

    if (storedData?.recordDate === dateNow || !storedData) {
      if (isPropertyPage) {
        const updatedPropertyRecords = findProperty
          ? propertyRecords.map((record) =>
              record.propertyUuid === uuidProperty
                ? {
                    propertyUuid: uuidProperty,
                    recordDate: dateNow,
                    menuCode: record.menuCode.includes(item.code)
                      ? record.menuCode
                      : [...record.menuCode, item.code],
                  }
                : record
            )
          : [
              ...propertyRecords,
              {
                recordDate: dateNow,
                propertyUuid: uuidProperty,
                menuCode: [item.code],
              },
            ];

        return setMenuClick({
          recordDate: dateNow,
          centralRecord: {
            recordDate: dateNow,
            menuCode: storedData?.centralRecord.menuCode || [],
          },
          propertyRecord: updatedPropertyRecords,
        });
      }

      const centralRecord = {
        recordDate: dateNow,
        menuCode: storedData?.centralRecord?.menuCode?.includes(item.code)
          ? storedData.centralRecord.menuCode
          : [...(storedData?.centralRecord?.menuCode || []), item.code],
      };

      return setMenuClick({
        recordDate: dateNow,
        centralRecord,
        propertyRecord: propertyRecords,
      });
    }
  };

  const getActive = () => {
    if (pathname.includes("/property/")) {
      if (!to.includes("/property/:propertyId/")) {
        const splitPath = pathname.split("/");
        const path = splitPath[1];
        const splitTo = to.split("/");
        const _to = splitTo[1];
        return _to === path;
      }

      const splitPath = pathname.split("/");
      const path = splitPath[3];

      const splitTo = to.split("/");
      const _to = splitTo[splitTo.length - 1];

      return _to === path;
    }

    if (pathname.includes(to) && !pathname.includes("/property/")) {
      const splitPath = pathname.split("/");
      const path = splitPath[1];
      const splitTo = to.split("/");
      const _to = splitTo[1];
      return _to === path;
    }

    return to === pathname;
  };

  const getNotificationQuantity = () => {
    const menuSlug = slug as MenuSlug;

    if (menuSlug !== "CHAT") {
      return undefined;
    }

    if (!menuSlug) return undefined;

    const notificationBySlug = notification?.[menuSlug];

    if (typeof notificationBySlug === "number") {
      return notificationBySlug;
    }

    if (typeof notificationBySlug === "object") {
      const notificationProperty = notificationBySlug as TPropertyNotification;

      if (property?.uuid) {
        return notificationProperty?.[property?.uuid];
      }
    }

    return undefined;
  };

  const active = getActive();
  const notificationQuantity = getNotificationQuantity();

  // console.log("ssss")
  // console.log(notificationQuantity)
  // console.log(notificationQuantity)

  const menu = (
    <SiderMenuItemWrapper $active={active}>
      <Row onClick={onNavigate}>
        <Col span={4} className="center">
          <Icon height={18} width={18} />
        </Col>
        <Col span={15} className="py-4 flex items-center">
          <Typography.Text ellipsis={{ tooltip: t(title, { ns: "menu" }) }}>
            {t(title, { ns: "menu" })}
          </Typography.Text>
        </Col>
        {notificationQuantity ? (
          <Col span={5} className="center">
            <NotificationItem className="center">
              {Number(notificationQuantity)}
            </NotificationItem>
          </Col>
        ) : null}
        
      </Row>
    </SiderMenuItemWrapper>
  );

  if (children && children.length) {
    return (
      <Popover
        overlayClassName="menu-popover"
        trigger={["click"]}
        showArrow={false}
        placement="rightTop"
        overlayInnerStyle={{ width: 280 }}
        content={
          <React.Fragment>
            {children.map((children) => {
              const active = children.to === pathname;

              const onNavigateChildren = () => {
                setMenuCode(children.code);
                setMenu(children);
                setActiveKey?.(undefined);

                if (propertyId) {
                  return navigate(
                    children.to.replace(":propertyId", propertyId),
                    {
                      replace: true,
                    }
                  );
                }

                return navigate(children.to, { replace: true });
              };

              return (
                <SiderMenuItemWrapper
                  key={children.key}
                  className="bg-gray"
                  $active={active}
                  // onClick={onClick}
                >
                  <Row
                    onClick={() => {
                      onNavigateChildren();
                      // navigate(item.to, { replace: true });
                    }}
                  >
                    <Col span={20} className="py-3 px-4">
                      <Typography.Text
                        ellipsis={{
                          tooltip: t(children.title, { ns: "menu" }),
                        }}
                      >
                        {t(children.title, { ns: "menu" })}
                      </Typography.Text>
                    </Col>
                    {/* {count && count.length > 0 && (
                      <Col span={4} className="center">
                        <NotificationItem className="center">
                          {Number(count.length)}
                        </NotificationItem>
                      </Col>
                    )} */}
                  </Row>
                </SiderMenuItemWrapper>
              );
            })}
          </React.Fragment>
        }
      >
        {menu}
      </Popover>
    );
  }
  return menu;
};

export default SiderMenuItem;
